import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { getLogoWhite } from "@/utils/assets";
import { ApplicationCheckout } from "@/components/ApplicationCheckout";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { supabase } from "@/integrations/supabase/client";
import { LanguageProvider, useLang } from "@/i18n/language";
import { PATHWAY_PRICES, portalContent, type CurrencyKey, type PathwayKey } from "@/i18n/portal";
import { previewCoupon } from "@/utils/coupons.functions";
import { formatDate } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/portal")({
  head: () => ({
    meta: [
      { title: "Mi portal STM | Share The Med" },
      {
        name: "description",
        content: "Sigue tu solicitud al programa Share The Med y tu pago de participación.",
      },
      { property: "og:title", content: "Mi portal STM | Share The Med" },
      {
        property: "og:description",
        content: "Solicitud, revisión académica y pago de participación en un solo lugar.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <LanguageProvider>
      <PortalPage />
    </LanguageProvider>
  ),
});

const inputClass =
  "w-full rounded-lg border border-border bg-background/60 px-4 py-2.5 text-sm outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary";
const cardClass = "rounded-xl border border-border bg-surface p-7";

const profileSchema = z.object({
  full_name: z.string().trim().min(2).max(120),
  country: z.string().trim().min(2).max(80),
  medical_area: z.string().trim().min(2).max(120),
});

const applicationSchema = z.object({
  pathway: z.enum(["basic", "intermediate", "advanced"]),
  currency: z.enum(["MXN", "USD"]),
  country: z.string().trim().min(2).max(80),
  medical_area: z.string().trim().min(2).max(120),
  notes: z.string().trim().max(1000).optional(),
});

function money(amount: number, currency: CurrencyKey) {
  return new Intl.NumberFormat(currency === "MXN" ? "es-MX" : "en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

function PortalPage() {
  const logo = getLogoWhite();
  const { lang, toggle } = useLang();
  const t = portalContent[lang].portal;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const profileQuery = useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const { data: auth } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, country, medical_area")
        .eq("id", auth.user!.id)
        .maybeSingle();
      if (error) throw error;
      return { email: auth.user?.email ?? "", profile: data };
    },
  });

  const appQuery = useQuery({
    queryKey: ["application"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("applications")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const roleQuery = useQuery({
    queryKey: ["my-roles"],
    queryFn: async () => {
      const { data, error } = await supabase.from("user_roles").select("role");
      if (error) throw error;
      return data.map((r) => r.role);
    },
  });

  const groupQuery = useQuery({
    queryKey: ["my-group"],
    queryFn: async () => {
      const { data: membership, error } = await supabase
        .from("group_members")
        .select("group_id, created_at")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      if (!membership) return null;
      const { data: group } = await supabase
        .from("groups")
        .select("name, pathway, starts_on, notes")
        .eq("id", membership.group_id)
        .maybeSingle();
      return group;
    },
  });

  const eventsQuery = useQuery({
    queryKey: ["my-application-events"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("application_events")
        .select("id, from_status, to_status, comment, created_at")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const [profile, setProfile] = useState({ full_name: "", country: "", medical_area: "" });
  const [pathway, setPathway] = useState<PathwayKey>("basic");
  const [currency, setCurrency] = useState<CurrencyKey>("MXN");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [checkout, setCheckout] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState<{ code: string; discount: string } | null>(null);
  const [couponBusy, setCouponBusy] = useState(false);

  useEffect(() => {
    const p = profileQuery.data?.profile;
    if (p) {
      setProfile({
        full_name: p.full_name ?? "",
        country: p.country ?? "",
        medical_area: p.medical_area ?? "",
      });
    }
  }, [profileQuery.data]);

  async function saveProfile(e: React.FormEvent) {
    e.preventDefault();
    const parsed = profileSchema.safeParse(profile);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Invalid data");
      return;
    }
    setBusy(true);
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from("profiles").upsert({ id: auth.user!.id, ...parsed.data });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t.saved);
    queryClient.invalidateQueries({ queryKey: ["profile"] });
  }

  async function submitApplication(e: React.FormEvent) {
    e.preventDefault();
    const parsed = applicationSchema.safeParse({
      pathway,
      currency,
      country: profile.country,
      medical_area: profile.medical_area,
      notes,
    });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Invalid data");
      return;
    }
    setBusy(true);
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from("applications").insert({
      user_id: auth.user!.id,
      pathway: parsed.data.pathway,
      currency: parsed.data.currency,
      country: parsed.data.country,
      medical_area: parsed.data.medical_area,
      notes: parsed.data.notes ?? null,
      amount: PATHWAY_PRICES[parsed.data.pathway][parsed.data.currency],
    });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t.submitted);
    setNotes("");
    queryClient.invalidateQueries({ queryKey: ["application"] });
  }

  async function signOut() {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  }

  const application = appQuery.data;
  const accepted = application?.status === "accepted";
  const paid = application?.status === "paid";

  async function applyCoupon(applicationId: string) {
    const code = couponInput.trim();
    if (code.length < 2) return;
    setCouponBusy(true);
    try {
      const result = await previewCoupon({ data: { applicationId, code } });
      if (!result.valid) {
        toast.error(t.coupon.errors[result.reason] ?? t.coupon.errors["not_found"]!);
        return;
      }
      const discount =
        result.discountType === "percent"
          ? `${result.percentOff}%`
          : new Intl.NumberFormat(lang === "es" ? "es-MX" : "en-US", {
              style: "currency",
              currency: result.currency ?? "MXN",
              maximumFractionDigits: 0,
            }).format(result.amountOff ?? 0);
      setCoupon({ code: result.code, discount });
      setCheckout(false);
      toast.success(
        t.coupon.applied.replace("{code}", result.code).replace("{discount}", discount),
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setCouponBusy(false);
    }
  }

  return (
    <>
      <PaymentTestModeBanner />
      <main className="mx-auto max-w-5xl px-5 py-12 md:px-8">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={logo.url} alt="Share The Med" className="h-9 w-auto" />
            <span className="text-sm font-semibold tracking-[0.22em] uppercase">
              Share <span className="text-primary">the</span> Med
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={toggle}
              className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold tracking-widest uppercase text-muted-foreground transition-colors hover:border-primary hover:text-primary"
            >
              {lang === "es" ? "EN" : "ES"}
            </button>
            {roleQuery.data?.includes("admin") && (
              <Link
                to="/admin"
                className="rounded-full border border-primary/50 px-4 py-1.5 text-xs font-semibold text-primary transition-opacity hover:opacity-80"
              >
                {t.adminLink}
              </Link>
            )}
            <button
              onClick={signOut}
              className="rounded-full border border-border px-4 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary hover:text-primary"
            >
              {t.signOut}
            </button>
          </div>
        </header>

        <div className="mt-12">
          <p className="eyebrow text-primary">{t.title}</p>
          <h1 className="mt-3 text-3xl font-semibold md:text-4xl">
            {t.greeting}
            {profile.full_name ? `, ${profile.full_name.split(" ")[0]}` : ""}.
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">{profileQuery.data?.email}</p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <form onSubmit={saveProfile} className={cardClass}>
            <h2 className="text-lg font-semibold">{t.profileTitle}</h2>
            <div className="mt-5 space-y-3">
              <input
                className={inputClass}
                placeholder={portalContent[lang].auth.fullName}
                value={profile.full_name}
                onChange={(e) => setProfile((p) => ({ ...p, full_name: e.target.value }))}
                maxLength={120}
              />
              <input
                className={inputClass}
                placeholder={portalContent[lang].auth.country}
                value={profile.country}
                onChange={(e) => setProfile((p) => ({ ...p, country: e.target.value }))}
                maxLength={80}
              />
              <input
                className={inputClass}
                placeholder={portalContent[lang].auth.medicalArea}
                value={profile.medical_area}
                onChange={(e) => setProfile((p) => ({ ...p, medical_area: e.target.value }))}
                maxLength={120}
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="mt-5 rounded-full border border-border px-5 py-2 text-sm font-semibold transition-colors hover:border-primary hover:text-primary disabled:opacity-60"
            >
              {t.save}
            </button>
          </form>

          {application ? (
            <div className={cardClass}>
              <div className="flex items-start justify-between gap-4">
                <h2 className="text-lg font-semibold">{t.statusTitle}</h2>
                <span className="rounded-full border border-primary/50 px-3 py-1 text-xs font-semibold text-primary">
                  {t.statuses[application.status]}
                </span>
              </div>
              <dl className="mt-5 space-y-3 text-sm">
                <div>
                  <dt className="text-xs tracking-widest uppercase text-muted-foreground">
                    {t.pathway}
                  </dt>
                  <dd className="mt-1">{t.pathways[application.pathway as PathwayKey]}</dd>
                </div>
                <div>
                  <dt className="text-xs tracking-widest uppercase text-muted-foreground">
                    {t.fee}
                  </dt>
                  <dd className="mt-1 text-primary">
                    {money(Number(application.amount ?? 0), application.currency as CurrencyKey)}
                  </dd>
                </div>
                {application.reviewer_notes && (
                  <div>
                    <dt className="text-xs tracking-widest uppercase text-muted-foreground">
                      {t.reviewNotes}
                    </dt>
                    <dd className="mt-1 text-muted-foreground">{application.reviewer_notes}</dd>
                  </div>
                )}
              </dl>

              <div className="mt-7 border-t border-border pt-6">
                <h3 className="text-sm font-semibold">{t.groupTitle}</h3>
                {groupQuery.data ? (
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    <span className="text-foreground">{groupQuery.data.name}</span>
                    {groupQuery.data.starts_on
                      ? ` · ${t.groupStarts}: ${formatDate(groupQuery.data.starts_on)}`
                      : ""}
                    {groupQuery.data.notes ? ` · ${groupQuery.data.notes}` : ""}
                  </p>
                ) : (
                  <p className="mt-2 text-sm text-muted-foreground">{t.groupNone}</p>
                )}
              </div>

              {(eventsQuery.data?.length ?? 0) > 0 && (
                <div className="mt-7 border-t border-border pt-6">
                  <h3 className="text-sm font-semibold">{t.historyTitle}</h3>
                  <ul className="mt-3 space-y-2">
                    {(eventsQuery.data ?? []).map((e) => (
                      <li key={e.id} className="text-xs leading-relaxed text-muted-foreground">
                        <span className="text-foreground/80">{formatDate(e.created_at)}</span>
                        {e.to_status ? ` — ${t.statuses[e.to_status]}` : ""}
                        {e.comment ? ` — ${e.comment}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-7 border-t border-border pt-6">
                <h3 className="text-sm font-semibold">{t.payTitle}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {paid ? t.paidLead : accepted ? t.payReadyLead : t.payLockedLead}
                </p>
                {!paid && accepted && (
                  <div className="mt-5 rounded-lg border border-border bg-background/40 p-4">
                    <label className="text-xs font-semibold text-muted-foreground">
                      {t.coupon.label}
                    </label>
                    {coupon ? (
                      <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                        <span className="rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-semibold tracking-widest text-primary uppercase">
                          {coupon.code} · −{coupon.discount}
                        </span>
                        <button
                          onClick={() => {
                            setCoupon(null);
                            setCouponInput("");
                            setCheckout(false);
                          }}
                          className="text-xs font-semibold text-muted-foreground underline"
                        >
                          {t.coupon.remove}
                        </button>
                      </div>
                    ) : (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <input
                          value={couponInput}
                          onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                          placeholder={t.coupon.placeholder}
                          className="min-w-[180px] flex-1 rounded-lg border border-border bg-background/60 px-4 py-2.5 text-sm tracking-widest uppercase outline-none focus:border-primary"
                        />
                        <button
                          onClick={() => applyCoupon(application.id)}
                          disabled={couponBusy}
                          className="rounded-full border border-border px-4 py-2 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary hover:text-primary disabled:opacity-50"
                        >
                          {t.coupon.apply}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {!paid && (
                  <button
                    disabled={!accepted}
                    onClick={() => setCheckout(true)}
                    className="mt-4 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-40"
                  >
                    {t.pay}
                  </button>
                )}
                {!paid && accepted && checkout && (
                  <ApplicationCheckout
                    key={coupon?.code ?? "no-coupon"}
                    applicationId={application.id}
                    {...(coupon ? { couponCode: coupon.code } : {})}
                    returnUrl={`${window.location.origin}/portal?paid=1`}
                  />
                )}
              </div>
            </div>
          ) : (
            <form onSubmit={submitApplication} className={cardClass}>
              <h2 className="text-lg font-semibold">{t.applyTitle}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.applyLead}</p>

              <div className="mt-5 space-y-4">
                <div>
                  <p className="mb-2 text-xs tracking-widest uppercase text-muted-foreground">
                    {t.pathway}
                  </p>
                  <div className="space-y-2">
                    {(Object.keys(PATHWAY_PRICES) as PathwayKey[]).map((key) => (
                      <button
                        type="button"
                        key={key}
                        onClick={() => setPathway(key)}
                        className={`flex w-full items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left text-sm transition-colors ${
                          pathway === key
                            ? "border-primary text-foreground"
                            : "border-border text-muted-foreground hover:border-primary/60"
                        }`}
                      >
                        <span>{t.pathways[key]}</span>
                        <span className="shrink-0 text-primary">
                          {money(PATHWAY_PRICES[key][currency], currency)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="mb-2 text-xs tracking-widest uppercase text-muted-foreground">
                    {t.currency}
                  </p>
                  <div className="flex gap-2">
                    {(["MXN", "USD"] as CurrencyKey[]).map((c) => (
                      <button
                        type="button"
                        key={c}
                        onClick={() => setCurrency(c)}
                        className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                          currency === c
                            ? "border-primary text-primary"
                            : "border-border text-muted-foreground hover:border-primary/60"
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>

                <textarea
                  className={`${inputClass} min-h-28 resize-y`}
                  placeholder={t.notesPlaceholder}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={1000}
                />
              </div>

              <button
                type="submit"
                disabled={busy}
                className="mt-5 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {t.submit}
              </button>
            </form>
          )}
        </div>
      </main>
    </>
  );
}
